import type { MonthSummary } from './types.js';

/**
 * 355: the dashboard's pure helpers. Every percentage and average is computed here from the server's sums, so the
 * page has one place that can be wrong and one test file that proves it is not.
 */

const whole = new Intl.NumberFormat('en-MY', { maximumFractionDigits: 0 });
const cents = new Intl.NumberFormat('en-MY', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `RM 11,270`: whole ringgit, for the big figures. */
export function ringgit(minor: number): string {
  return `RM ${whole.format(Math.round(minor / 100))}`;
}

/** `RM 96.00`: ringgit and sen, where the cents matter (amounts paid, averages). */
export function ringgitCents(minor: number): string {
  return `RM ${cents.format(minor / 100)}`;
}

export type ChangeTone = 'up' | 'down' | 'flat' | 'new';

/** The change against last month's same days (FR-016). A zero base is "new", never Infinity. */
export function changeLabel(current: number, previous: number): { text: string; tone: ChangeTone } {
  if (previous === 0)
    return current === 0 ? { text: '0%', tone: 'flat' } : { text: 'new', tone: 'new' };
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return { text: '0%', tone: 'flat' };
  return pct > 0
    ? { text: `▲ ${pct}%`, tone: 'up' }
    : { text: `▼ ${Math.abs(pct)}%`, tone: 'down' };
}

/** Revenue over rentals; "—" when there are none (FR-015). */
export function averageRental(revenueMinor: number, rentals: number): string {
  return rentals > 0 ? ringgitCents(Math.round(revenueMinor / rentals)) : '—';
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** `2026-10` → `October 2026`. */
export function monthName(label: string): string {
  const [y, m] = label.split('-').map(Number) as [number, number];
  return `${MONTHS[m - 1]} ${y}`;
}

export interface ChartPoint {
  label: string;
  day: string;
  kind: 'past' | 'today' | 'ahead';
  /** Ringgit. */
  revenue: number;
  /** Ringgit, last month's same day; `null` past last month's length. */
  previous: number | null;
}

/** One point per day: days before today are picked up, today is today, later days are booked ahead (FR-018). */
export function chartSeries(daily: MonthSummary['daily'], today: string): ChartPoint[] {
  return daily.map((d) => ({
    label: String(Number(d.day.slice(8, 10))),
    day: d.day,
    kind: d.day < today ? 'past' : d.day === today ? 'today' : 'ahead',
    revenue: d.revenue_minor / 100,
    previous: d.prev_revenue_minor === null ? null : d.prev_revenue_minor / 100,
  }));
}

/** The chart's text alternative (FR-028): the month's total and its highest day. */
export function chartSummary(daily: MonthSummary['daily']): string {
  const first = daily[0];
  if (!first) return '';
  const month = monthName(first.day.slice(0, 7));
  const total = daily.reduce((t, d) => t + d.revenue_minor, 0);
  const top = daily.reduce((best, d) => (d.revenue_minor > best.revenue_minor ? d : best), first);
  if (total === 0) return `${month} revenue ${ringgit(0)}. No paid rentals yet this month.`;
  const topDay = `${Number(top.day.slice(8, 10))} ${MONTHS[Number(top.day.slice(5, 7)) - 1]}`;
  return `${month} revenue ${ringgit(total)}. Highest day: ${topDay}, ${ringgit(top.revenue_minor)}.`;
}
